import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Search, ChevronDown, X, Loader2, Check, Inbox } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function SelectField({
  value = null,
  onChange,
  fetchData,
  preload = false,
  labelKey = "label",
  valueKey = "value",
  placeholder = "select",
  searchPlaceholder = "search",
  searchParams = {},
  searchFields = null,
  pageSize = 20,
  debounceMs = 300,
  disabled = false,
  clearable = true,
  renderOption = null,
  renderValue = null,
  noResultsText = "no_results",
  emptyText = "empty_options",
  className = "",
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasPreloaded, setHasPreloaded] = useState(false);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const abortRef = useRef(null);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  // ---- debounce ----
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), debounceMs);
    return () => clearTimeout(t);
  }, [search, debounceMs]);

  // ---- keep selectedItem in sync with value ----
  useEffect(() => {
    if (value == null) {
      setSelectedItem(null);
      return;
    }
    const found = items.find((i) => i[valueKey] === value);
    if (found) setSelectedItem(found);
  }, [value, items, valueKey]);

  // ---- fetcher ----
  const loadData = useCallback(
    async (pageNum, searchTerm, isLoadMore = false) => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      if (isLoadMore) setLoadingMore(true);
      else setLoading(true);

      try {
        const result = await fetchData({
          search: searchTerm,
          page: pageNum,
          limit: preload ? 9999 : pageSize,
          signal: abortRef.current.signal,
          ...searchParamsRef.current,
        });

        const data = result?.data ?? [];
        const more = result?.hasMore ?? data.length === pageSize;

        setItems((prev) => (pageNum === 1 ? data : [...prev, ...data]));
        setHasMore(more);
        setPage(pageNum);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Dropdown fetch error:", e);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [fetchData, pageSize, preload]
  );

  // ---- preload mode: fetch once on mount ----
  useEffect(() => {
    if (preload && !hasPreloaded) {
      setHasPreloaded(true);
      loadData(1, "");
    }
  }, [preload, hasPreloaded, loadData]);

  // ---- lazy mode: (re)fetch when opened or when search changes ----
  useEffect(() => {
    if (preload) return;
    if (!isOpen) return;
    loadData(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isOpen, preload]);

  // ---- click outside ----
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ---- focus search on open ----
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // ---- client-side filter for preload mode ----
  const displayedItems = useMemo(() => {
    if (!preload || !debouncedSearch) return items;
    const fields =
      searchFields && searchFields.length ? searchFields : [labelKey];
    const q = debouncedSearch.toLowerCase();
    return items.filter((item) =>
      fields.some((f) =>
        String(item[f] ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [items, debouncedSearch, preload, searchFields, labelKey]);

  // ---- handlers ----
  const selectItem = (item) => {
    setSelectedItem(item);
    onChange?.(item[valueKey], item);
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(-1);
  };

  const clearValue = (e) => {
    e.stopPropagation();
    setSelectedItem(null);
    onChange?.(null, null);
  };

  const onScroll = (e) => {
    if (preload || !hasMore || loadingMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 48) {
      loadData(page + 1, debouncedSearch, true);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, displayedItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = displayedItems[highlightedIndex];
      if (item) selectItem(item);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const selectedLabel = selectedItem
    ? renderValue
      ? renderValue(selectedItem)
      : selectedItem[labelKey]
    : "";

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-start bg-white border rounded-xl transition-all duration-150
          ${
            isOpen
              ? "border-slate-900 ring-4 ring-slate-900/5"
              : "border-slate-200 hover:border-slate-300"
          }
          ${disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : "cursor-pointer"}
        `}
      >
        <span
          className={`truncate text-sm ${
            selectedItem ? "text-slate-900" : "text-slate-400"
          }`}
        >
          {t(selectedLabel) || t(placeholder)}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {clearable && selectedItem && !disabled && (
            <span
              role="button"
              onClick={clearValue}
              className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown
            size={16}
            className={`text-slate-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {isOpen && (
        <div
          className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-900/10 overflow-hidden"
          style={{ animation: "dropdownIn 160ms ease-out" }}
        >
          <style>{`
            @keyframes dropdownIn {
              from { opacity: 0; transform: translateY(-4px) scale(0.99); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>

          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
            <Search size={15} className="text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setHighlightedIndex(-1);
              }}
              onKeyDown={onKeyDown}
              placeholder={t(searchPlaceholder)}
              className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 bg-transparent border-0 outline-none"
            />
            {loading && (
              <Loader2 size={14} className="text-slate-400 animate-spin" />
            )}
          </div>

          <div
            ref={listRef}
            onScroll={onScroll}
            className="max-h-64 overflow-y-auto overscroll-contain"
          >
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
                <Loader2 size={14} className="animate-spin" />
                {t("loading")}
              </div>
            ) : displayedItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-1.5 py-8 text-sm text-slate-400">
                <Inbox size={20} />
                <span>{debouncedSearch ? t(noResultsText) : t(emptyText)}</span>
              </div>
            ) : (
              <>
                {displayedItems.map((item, idx) => {
                  const isSelected =
                    selectedItem && selectedItem[valueKey] === item[valueKey];
                  const isHighlighted = highlightedIndex === idx;
                  return (
                    <div
                      key={item[valueKey]}
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      className={`flex items-center justify-between gap-2 px-3 py-2 cursor-pointer text-sm transition-colors
                        ${isHighlighted ? "bg-slate-100" : ""}
                        ${isSelected ? "text-slate-900 font-medium" : "text-slate-700"}
                      `}
                    >
                      <div className="flex-1 min-w-0 truncate">
                        {renderOption ? renderOption(item) : item[labelKey]}
                      </div>
                      {isSelected && (
                        <Check size={14} className="text-slate-900 shrink-0" />
                      )}
                    </div>
                  );
                })}
                {loadingMore && (
                  <div className="flex items-center justify-center gap-2 py-3 text-xs text-slate-400">
                    <Loader2 size={12} className="animate-spin" />
                    {t("loading_more")}
                  </div>
                )}
                {!preload && !hasMore && items.length > 0 && (
                  <div className="py-2 text-center text-xs text-slate-400">
                    {t("end_of_results")}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

